/**
 * Touch controls, the DOM half: elements, pointers and nothing else.
 *
 * Every decision this makes is deferred to ./touch-model.ts, which is pure and
 * tested. What is left here is the part that cannot be tested in Node: a
 * stylesheet, a handful of divs, and the pointer bookkeeping that turns three
 * simultaneous fingers into one `TouchState`.
 *
 * ===========================================================================
 * WHY POINTER EVENTS, AND WHY EVERY PRESS IS CAPTURED
 * ===========================================================================
 *
 * `pointerdown`/`pointermove`/`pointerup` unify mouse and touch, which is what
 * lets the layout be checked on a desktop with a mouse instead of only on a
 * phone. They also carry a `pointerId`, and that is what makes multi-touch
 * bookkeeping a map lookup rather than a scan of `TouchList` deltas.
 *
 * Every press calls `setPointerCapture`. Without it a thumb that slides off
 * the boost button — which happens constantly, because the player is looking
 * at the ball and not at their hand — delivers its `pointerup` to whatever
 * element is under it instead, and the button stays lit and held forever. With
 * capture, the element that received the press receives the release, and
 * `lostpointercapture` covers the cases the browser takes it back (a system
 * gesture, an incoming call). A stuck throttle is the touch equivalent of the
 * stuck key `window.blur` exists to clear in ./controller.ts, and it has the
 * same cure: one place that releases everything.
 *
 * ===========================================================================
 * THE LAYOUT
 * ===========================================================================
 *
 * Landscape, two thumbs, and the ball is in the middle of the screen — so
 * nothing sits above the lower third except the two smallest buttons.
 *
 *   left  half   floating stick. See the header of ./touch-model.ts for why
 *                it floats and why the throttle is not on it.
 *   bottom right boost, jump, roll, brake — the four that are held or stabbed
 *                mid-play, largest first, boost in the corner where the thumb
 *                already rests.
 *   above them   ball cam and (when auto-throttle is off) drive, both taps.
 *   top left     pause, deliberately far from everything and small. There is
 *                no Escape key on a phone, so without this button a match
 *                cannot be left at all.
 *
 * Sizes come from one `--rw-tb` unit that scales with the viewport, so the
 * cluster is thumb-sized on a phone and does not become a wall of dinner
 * plates on a tablet. The stick radius is set from script rather than CSS
 * because the same number has to reach `stickVector`, and a value read back
 * out of a computed style is a value that can disagree with the one drawn.
 */

import {
  TOUCH_BUTTONS,
  TOUCH_BUTTON_IDS,
  noTouchButtons,
  stickVector,
  type TouchButtonId,
  type TouchState,
} from './touch-model';

/** Above the HUD (z 10), below the menu overlay (z 20 — see ../ui/style.ts). */
export const TOUCH_Z_INDEX = 12;

/**
 * Full-deflection distance, in CSS pixels, as a fraction of the SHORTER
 * viewport side. Tied to the short side because that is the one a landscape
 * phone is tight on, and a stick whose ring runs off the bottom of the screen
 * cannot be pushed to full travel downwards.
 */
const STICK_RADIUS_FRACTION = 0.16;
const STICK_RADIUS_MIN = 52;
const STICK_RADIUS_MAX = 96;

const stickRadiusFor = (width: number, height: number): number =>
  Math.max(STICK_RADIUS_MIN, Math.min(STICK_RADIUS_MAX, Math.min(width, height) * STICK_RADIUS_FRACTION));

const STYLE = `
.rw-touch {
  position: absolute;
  inset: 0;
  z-index: ${TOUCH_Z_INDEX};
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: none;
  font-family: 'Inter', 'SF Pro Display', 'Segoe UI Variable Display', system-ui,
    -apple-system, 'Helvetica Neue', Arial, sans-serif;
  color: #eef4ff;
  --rw-tb: clamp(46px, 13vh, 74px);
  --rw-stick-radius: 72px;
}
.rw-touch[hidden] { display: none; }
.rw-touch * { box-sizing: border-box; }

/* Spectating — bot-vs-bot, or a seat the server did not give us. There is no
   car to drive, so the stick and the buttons would be controls that do
   nothing. The pause button stays: leaving is still the player's to do, and
   on a phone this is the only way to. */
.rw-touch[data-spectate='on'] .rw-touch-zone,
.rw-touch[data-spectate='on'] .rw-touch-right { display: none; }

/* --- the stick --------------------------------------------------------- */

/* The whole lower-left quadrant is the stick, not the ring that is drawn on
   it: the ring is feedback about a press that has already been accepted. The
   zone stops short of the top so the pause button and the score are not behind
   a surface that swallows presses. */
.rw-touch-zone {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 46%;
  height: 74%;
  pointer-events: auto;
  touch-action: none;
}
.rw-touch-stick {
  position: absolute;
  left: 0;
  top: 0;
  width: calc(var(--rw-stick-radius) * 2);
  height: calc(var(--rw-stick-radius) * 2);
  margin-left: calc(var(--rw-stick-radius) * -1);
  margin-top: calc(var(--rw-stick-radius) * -1);
  border-radius: 50%;
  border: 2px solid rgba(198, 214, 240, 0.28);
  background: radial-gradient(circle at 50% 50%, rgba(10, 15, 26, 0.42) 0%, rgba(10, 15, 26, 0.12) 70%, rgba(10, 15, 26, 0) 100%);
  opacity: 0;
  transition: opacity 120ms linear;
  will-change: transform, opacity;
}
.rw-touch-stick.on { opacity: 1; }
.rw-touch-knob {
  position: absolute;
  left: 50%;
  top: 50%;
  width: calc(var(--rw-stick-radius) * 0.78);
  height: calc(var(--rw-stick-radius) * 0.78);
  margin-left: calc(var(--rw-stick-radius) * -0.39);
  margin-top: calc(var(--rw-stick-radius) * -0.39);
  border-radius: 50%;
  border: 2px solid rgba(226, 238, 255, 0.6);
  background: rgba(20, 30, 48, 0.6);
  backdrop-filter: blur(3px);
  will-change: transform;
}

/* --- buttons ------------------------------------------------------------ */

.rw-touch-right {
  position: absolute;
  right: calc(18px + env(safe-area-inset-right, 0px));
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  width: calc(var(--rw-tb) * 4.4);
  height: calc(var(--rw-tb) * 3.0);
  pointer-events: none;
}
.rw-tbtn {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  aspect-ratio: 1;
  border-radius: 50%;
  border: 2px solid rgba(198, 214, 240, 0.34);
  background: rgba(9, 13, 22, 0.5);
  backdrop-filter: blur(4px);
  pointer-events: auto;
  touch-action: none;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  line-height: 1;
  text-align: center;
  /* No transition on the press state. The whole point of the highlight is to
     tell the player the tap registered, and 80 ms of fade is 80 ms of doubt. */
  transition: border-color 90ms linear;
}
.rw-tbtn[hidden] { display: none; }
.rw-tbtn-label { font-size: calc(var(--rw-tb) * 0.19); }
.rw-tbtn-sub {
  font-size: calc(var(--rw-tb) * 0.135);
  font-weight: 600;
  letter-spacing: 0.14em;
  color: rgba(190, 208, 235, 0.6);
}
.rw-tbtn.on {
  background: rgba(226, 238, 255, 0.22);
  border-color: rgba(232, 242, 255, 0.9);
}

.rw-tbtn[data-touch='boost'] {
  right: 0;
  bottom: 0;
  width: calc(var(--rw-tb) * 1.55);
  border-color: rgba(255, 194, 58, 0.5);
  color: #ffd97a;
}
.rw-tbtn[data-touch='boost'].on {
  background: rgba(255, 194, 58, 0.3);
  border-color: #ffc23a;
}
.rw-tbtn[data-touch='jump'] {
  right: calc(var(--rw-tb) * 1.75);
  bottom: calc(var(--rw-tb) * 0.12);
  width: calc(var(--rw-tb) * 1.3);
}
.rw-tbtn[data-touch='airRoll'] {
  right: calc(var(--rw-tb) * 0.18);
  bottom: calc(var(--rw-tb) * 1.75);
  width: calc(var(--rw-tb) * 1.1);
}
.rw-tbtn[data-touch='brake'] {
  right: calc(var(--rw-tb) * 1.9);
  bottom: calc(var(--rw-tb) * 1.6);
  width: calc(var(--rw-tb) * 1.1);
}
.rw-tbtn[data-touch='throttle'] {
  right: calc(var(--rw-tb) * 3.2);
  bottom: calc(var(--rw-tb) * 0.35);
  width: calc(var(--rw-tb) * 1.05);
}
.rw-tbtn[data-touch='ballCam'] {
  right: calc(var(--rw-tb) * 3.25);
  bottom: calc(var(--rw-tb) * 1.7);
  width: calc(var(--rw-tb) * 0.95);
}
.rw-tbtn[data-touch='ballCam'] .rw-tbtn-label { font-size: calc(var(--rw-tb) * 0.14); }

/* --- pause -------------------------------------------------------------- */

.rw-touch-pause {
  position: absolute;
  top: calc(14px + env(safe-area-inset-top, 0px));
  left: calc(14px + env(safe-area-inset-left, 0px));
  width: calc(var(--rw-tb) * 0.85);
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(var(--rw-tb) * 0.08);
  border-radius: 50%;
  border: 2px solid rgba(198, 214, 240, 0.3);
  background: rgba(9, 13, 22, 0.5);
  backdrop-filter: blur(4px);
  pointer-events: auto;
  touch-action: none;
}
.rw-touch-pause i {
  display: block;
  width: calc(var(--rw-tb) * 0.07);
  height: calc(var(--rw-tb) * 0.28);
  background: rgba(226, 238, 255, 0.85);
}
.rw-touch-pause.on { background: rgba(226, 238, 255, 0.22); }
`;

export interface TouchControlsOptions {
  /** The pause menu has no keyboard to be opened from. See the header. */
  onPause(): void;
  /** Whether the car drives on its own; see `touchCarInput`. */
  autoThrottle?: boolean;
}

/**
 * The live on-screen controls.
 *
 * `read()` returns the SAME object every call, mutated in place. It is read
 * once per physics tick — 120 times a second — and a fresh object per tick is
 * 120 allocations a second for a value nothing keeps.
 */
export class TouchControls {
  readonly root: HTMLDivElement;

  private readonly styleEl: HTMLStyleElement;
  private readonly zone: HTMLDivElement;
  private readonly stickEl: HTMLDivElement;
  private readonly knobEl: HTMLDivElement;
  private readonly buttonEls = new Map<TouchButtonId, HTMLElement>();
  private readonly pauseEl: HTMLElement;

  /** Live, mutated in place. See the class note. */
  private readonly state: {
    stick: { active: boolean; x: number; y: number };
    buttons: Record<TouchButtonId, boolean>;
  } = { stick: { active: false, x: 0, y: 0 }, buttons: noTouchButtons() };

  /** The pointer that owns the stick, and where it first landed. */
  private stickPointer: number | null = null;
  private stickOriginX = 0;
  private stickOriginY = 0;
  /** Which button each captured pointer is holding. */
  private readonly pointerButtons = new Map<number, TouchButtonId>();

  private radius = STICK_RADIUS_MIN;
  private autoThrottle: boolean;
  private readonly onResize: () => void;
  private readonly onBlur: () => void;

  constructor(container: HTMLElement, private readonly options: TouchControlsOptions) {
    this.autoThrottle = options.autoThrottle ?? true;

    this.styleEl = document.createElement('style');
    this.styleEl.textContent = STYLE;
    document.head.appendChild(this.styleEl);

    this.root = document.createElement('div');
    this.root.className = 'rw-touch';
    this.root.innerHTML = `
      <div class="rw-touch-zone">
        <div class="rw-touch-stick"><div class="rw-touch-knob"></div></div>
      </div>
      <div class="rw-touch-right">
        ${TOUCH_BUTTONS.map(
          spec => `
        <div class="rw-tbtn" data-touch="${spec.id}">
          <div class="rw-tbtn-label">${spec.label}</div>
          ${spec.sub === undefined ? '' : `<div class="rw-tbtn-sub">${spec.sub}</div>`}
        </div>`
        ).join('')}
      </div>
      <div class="rw-touch-pause" aria-label="Pause"><i></i><i></i></div>
    `;
    container.appendChild(this.root);

    const q = <T extends Element>(selector: string): T => {
      const el = this.root.querySelector(selector);
      if (!el) throw new Error(`touch element missing: ${selector}`);
      return el as T;
    };
    this.zone = q<HTMLDivElement>('.rw-touch-zone');
    this.stickEl = q<HTMLDivElement>('.rw-touch-stick');
    this.knobEl = q<HTMLDivElement>('.rw-touch-knob');
    this.pauseEl = q<HTMLElement>('.rw-touch-pause');
    for (const id of TOUCH_BUTTON_IDS) {
      this.buttonEls.set(id, q<HTMLElement>(`.rw-tbtn[data-touch='${id}']`));
    }

    this.bindStick();
    for (const [id, el] of this.buttonEls) this.bindButton(id, el);
    this.bindPause();

    // A long press on any of this would otherwise raise the text-selection or
    // image menu over the controls, which cannot then be dismissed by playing.
    this.root.addEventListener('contextmenu', e => e.preventDefault());

    this.onResize = () => this.measure();
    window.addEventListener('resize', this.onResize);
    // Same failure as a stuck key, same cure — see the header.
    this.onBlur = () => this.releaseAll();
    window.addEventListener('blur', this.onBlur);

    this.measure();
    this.setAutoThrottle(this.autoThrottle);
  }

  // -------------------------------------------------------------------------
  // Reading
  // -------------------------------------------------------------------------

  /** The live state. Mutated in place between calls — do not retain. */
  read(): TouchState {
    return this.state;
  }

  /** Whether the car drives with no button held. Mirrors the setting. */
  get driveOnItsOwn(): boolean {
    return this.autoThrottle;
  }

  setAutoThrottle(on: boolean): void {
    this.autoThrottle = on;
    const throttle = this.buttonEls.get('throttle');
    if (throttle) throttle.hidden = on;
    // A button that has just been hidden cannot deliver its release.
    if (on) this.state.buttons.throttle = false;
  }

  /**
   * Show or hide the controls. Hiding RELEASES everything: the overlay goes
   * away when the pause menu opens, and a boost held at that moment would
   * still be held when play resumed, with no button on screen to let go of.
   */
  setVisible(visible: boolean): void {
    if (this.root.hidden === !visible) return;
    this.root.hidden = !visible;
    if (!visible) this.releaseAll();
  }

  get visible(): boolean {
    return !this.root.hidden;
  }

  /** Drop the driving half; keep the pause button. See the stylesheet. */
  setSpectating(on: boolean): void {
    if (on) this.root.dataset.spectate = 'on';
    else delete this.root.dataset.spectate;
    if (on) this.releaseAll();
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('blur', this.onBlur);
    this.root.remove();
    this.styleEl.remove();
  }

  // -------------------------------------------------------------------------
  // Pointers
  // -------------------------------------------------------------------------

  private measure(): void {
    this.radius = stickRadiusFor(window.innerWidth, window.innerHeight);
    this.root.style.setProperty('--rw-stick-radius', `${this.radius.toFixed(1)}px`);
  }

  private bindStick(): void {
    this.zone.addEventListener('pointerdown', event => {
      // One finger owns the stick. A second one landing in the same quadrant
      // is a palm or a stray, and stealing the stick for it would swing the
      // car to wherever that stray happened to be.
      if (this.stickPointer !== null) return;
      event.preventDefault();
      this.stickPointer = event.pointerId;
      this.stickOriginX = event.clientX;
      this.stickOriginY = event.clientY;
      this.state.stick.active = true;
      this.state.stick.x = 0;
      this.state.stick.y = 0;

      const rect = this.zone.getBoundingClientRect();
      this.stickEl.style.transform = `translate(${event.clientX - rect.left}px, ${event.clientY - rect.top}px)`;
      this.stickEl.classList.add('on');
      this.knobEl.style.transform = 'translate(0px, 0px)';
      this.zone.setPointerCapture(event.pointerId);
    });

    this.zone.addEventListener('pointermove', event => {
      if (event.pointerId !== this.stickPointer) return;
      event.preventDefault();
      const dx = event.clientX - this.stickOriginX;
      const dy = event.clientY - this.stickOriginY;
      const v = stickVector(dx, dy, this.radius);
      this.state.stick.x = v.x;
      this.state.stick.y = v.y;

      // The knob is drawn at the RAW deflection clamped to the ring, not at
      // the curved value: it is showing the player where their thumb is, and a
      // knob that sat still through the deadzone would read as a dropped touch.
      const distance = Math.hypot(dx, dy);
      const scale = distance > this.radius ? this.radius / distance : 1;
      this.knobEl.style.transform = `translate(${(dx * scale).toFixed(1)}px, ${(dy * scale).toFixed(1)}px)`;
    });

    const release = (event: PointerEvent): void => {
      if (event.pointerId !== this.stickPointer) return;
      this.releaseStick();
    };
    this.zone.addEventListener('pointerup', release);
    this.zone.addEventListener('pointercancel', release);
    this.zone.addEventListener('lostpointercapture', release);
  }

  private bindButton(id: TouchButtonId, el: HTMLElement): void {
    el.addEventListener('pointerdown', event => {
      event.preventDefault();
      this.pointerButtons.set(event.pointerId, id);
      this.state.buttons[id] = true;
      el.classList.add('on');
      el.setPointerCapture(event.pointerId);
    });

    const release = (event: PointerEvent): void => {
      if (this.pointerButtons.get(event.pointerId) !== id) return;
      this.pointerButtons.delete(event.pointerId);
      this.state.buttons[id] = false;
      el.classList.remove('on');
    };
    el.addEventListener('pointerup', release);
    el.addEventListener('pointercancel', release);
    el.addEventListener('lostpointercapture', release);
  }

  private bindPause(): void {
    // On `pointerup`, not `pointerdown`: this is the one control whose effect
    // is irreversible mid-play, and a menu that opens from a finger brushing
    // the corner is worse than one that takes a deliberate tap.
    this.pauseEl.addEventListener('pointerdown', event => {
      event.preventDefault();
      this.pauseEl.classList.add('on');
      this.pauseEl.setPointerCapture(event.pointerId);
    });
    const clear = (): void => this.pauseEl.classList.remove('on');
    this.pauseEl.addEventListener('pointerup', event => {
      event.preventDefault();
      clear();
      this.options.onPause();
    });
    this.pauseEl.addEventListener('pointercancel', clear);
    this.pauseEl.addEventListener('lostpointercapture', clear);
  }

  private releaseStick(): void {
    this.stickPointer = null;
    this.state.stick.active = false;
    this.state.stick.x = 0;
    this.state.stick.y = 0;
    this.stickEl.classList.remove('on');
  }

  /** Everything up, everything unlit. The one recovery from a lost pointer. */
  private releaseAll(): void {
    this.releaseStick();
    this.pointerButtons.clear();
    for (const id of TOUCH_BUTTON_IDS) {
      this.state.buttons[id] = false;
      this.buttonEls.get(id)?.classList.remove('on');
    }
    this.pauseEl.classList.remove('on');
  }
}
